import React from 'react';
import { Badge, Button, Spinner } from 'reactstrap';

import { ClickableIcon, Toolbar } from '~components';
import { faCog } from '~components/icon';

import { AuthTags } from '~constants/authTags';

import { FormTemplate, FormTemplateVersion } from '~forms/types';

interface ComponentProps {
  template: FormTemplate;
  versions: FormTemplateVersion[];
  selectedVersionId: string | null;
  onSelectVersion: (versionId: string) => void;
  hasOpenDraft: boolean;
  canEdit: boolean;
  isPublishing: boolean;
  isCreatingVersion: boolean;
  onPublish: () => void;
  onCreateVersion: () => void;
  onPreview: () => void;
  onOpenSettings: () => void;
}

const versionLabel = (version: FormTemplateVersion): string =>
  `v${version.version_number} - ${version.published_at ? 'published' : 'draft'}`;

const FormTopBar: React.FC<ComponentProps> = ({
  template,
  versions,
  selectedVersionId,
  onSelectVersion,
  hasOpenDraft,
  canEdit,
  isPublishing,
  isCreatingVersion,
  onPublish,
  onCreateVersion,
  onPreview,
  onOpenSettings,
}) => {
  // Versions newest-first for the selector.
  const orderedVersions = [...versions].sort((a, b) => b.version_number - a.version_number);
  const selectedVersion = versions.find((v) => v.id === selectedVersionId) ?? null;
  const selectedIsPublished = !!selectedVersion?.published_at;

  // Title, description and the care-worker fill flag are edited in the Form Settings modal (cog icon).
  // The cog is gated on the Create tag, so non-editors don't see it.
  const settingsIcon = (
    <ClickableIcon
      key="form-builder-settings-btn"
      id="form-builder-settings-btn"
      icon={faCog}
      className="me-2"
      toolTip="Form settings"
      onClick={onOpenSettings}
      authTag={AuthTags.FORMS.CUSTOMFORMS.CREATE}
      dataCy="form-builder-settings-icon"
      ariaLabel="Form settings"
    />
  );

  const previewButton = (
    <Button
      key="form-builder-preview-btn"
      color="info"
      outline
      size="sm"
      className="me-2"
      onClick={onPreview}
      data-cy="form-builder-preview-btn"
      aria-label="Preview form"
    >
      Preview
    </Button>
  );

  const publishButton = (
    <Button
      key="form-builder-publish-btn"
      color="success"
      size="sm"
      onClick={onPublish}
      disabled={isPublishing}
      data-cy="form-builder-publish-btn"
      aria-label="Publish form"
    >
      {isPublishing ? (
        <>
          <Spinner size="sm" className="me-1" />
          Publishing...
        </>
      ) : (
        'Publish'
      )}
    </Button>
  );

  const createVersionButton = (
    <Button
      key="form-builder-create-version-btn"
      color="success"
      size="sm"
      onClick={onCreateVersion}
      disabled={isCreatingVersion}
      data-cy="form-builder-create-version-btn"
      aria-label="Create new version"
    >
      {isCreatingVersion ? (
        <>
          <Spinner size="sm" className="me-1" />
          Creating...
        </>
      ) : (
        'Create New Version'
      )}
    </Button>
  );

  // Settings + Preview are always available; the publish/version action requires edit permission.
  const actionButtons = [settingsIcon, previewButton];
  if (canEdit) {
    actionButtons.push(hasOpenDraft ? publishButton : createVersionButton);
  }

  return (
    <Toolbar
      id="form-builder-top-bar"
      leftCluster={
        <div className="d-flex align-items-center gap-2">
          <span
            className="form-builder-title-display"
            aria-label={`Form title: ${template.title}`}
            data-cy="form-builder-title-display"
          >
            {template.title}
          </span>
          <select
            className="form-select form-select-sm form-builder-version-select"
            value={selectedVersionId ?? ''}
            onChange={(e) => onSelectVersion(e.target.value)}
            aria-label="Select version"
            data-cy="form-builder-version-select"
          >
            {orderedVersions.map((version) => (
              <option key={version.id} value={version.id}>
                {versionLabel(version)}
              </option>
            ))}
          </select>
          <Badge color={selectedIsPublished ? 'success' : 'warning'} pill>
            {selectedIsPublished ? 'Published' : 'Draft'}
          </Badge>
        </div>
      }
      rightCluster={<>{actionButtons}</>}
    />
  );
};

export default FormTopBar;
